import { useMemo } from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  Legend
} from 'recharts';
import { ChartBarIcon, ClockIcon, UserGroupIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

const TEACHER_COLORS = ['#60a5fa', '#f87171', '#fbbf24', '#34d399', '#a78bfa', '#f472b6', '#fb923c'];

const StudentAnalytics = ({ mentions, consequences, messages, eventTypeConfig }) => {
  // Check if we have enough data to show analytics
  const hasEnoughData = mentions && mentions.length >= 3;

  // 1. Mentions by Day of Week
  const dayOfWeekData = useMemo(() => {
    if (!mentions || mentions.length === 0) return [];

    const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
    const counts = { Mon: 0, Tue: 0, Wed: 0, Thu: 0, Fri: 0 };

    mentions.forEach(mention => {
      // mentions are already Date objects from StudentDashboard
      const date = mention.issuedAt instanceof Date ? mention.issuedAt : new Date(mention.issuedAt);
      if (date) {
        const dayIndex = date.getDay(); // 0 = Sunday, 1 = Monday, etc.
        if (dayIndex >= 1 && dayIndex <= 5) {
          counts[days[dayIndex - 1]]++;
        }
      }
    });

    return days.map(day => ({ day, count: counts[day] }));
  }, [mentions]);

  // 2. Mentions by Offense Type
  const offenseData = useMemo(() => {
    if (!mentions || mentions.length === 0) return [];

    const offenseCounts = {};

    mentions.forEach(mention => {
      const offense = mention.offense || mention.reason || mention.description || 'Unspecified';
      offenseCounts[offense] = (offenseCounts[offense] || 0) + 1;
    });

    // Sort by count and get top 3
    const sorted = Object.entries(offenseCounts)
      .map(([offense, count]) => ({
        offense,
        count,
        color: TEACHER_COLORS[Object.keys(offenseCounts).indexOf(offense) % TEACHER_COLORS.length]
      }))
      .sort((a, b) => b.count - a.count);

    // Keep top 3, group rest as "Other"
    if (sorted.length > 3) {
      const top3 = sorted.slice(0, 3);
      const others = sorted.slice(3);
      const otherCount = others.reduce((sum, item) => sum + item.count, 0);

      if (otherCount > 0) {
        top3.push({ offense: 'Other', count: otherCount, color: '#94a3b8' });
      }

      return top3;
    }

    return sorted;
  }, [mentions]);

  // 3. Mentions by Teacher
  const teacherData = useMemo(() => {
    if (!mentions || mentions.length === 0) return [];

    const teacherCounts = {};

    mentions.forEach(mention => {
      const teacher = mention.issuedByName || mention.issuedBy || 'Unknown';
      teacherCounts[teacher] = (teacherCounts[teacher] || 0) + 1;
    });

    // Sort by count and get top 7
    const sorted = Object.entries(teacherCounts)
      .map(([teacher, count]) => ({ teacher, count }))
      .sort((a, b) => b.count - a.count);

    // Keep top 7, group rest as "Others"
    if (sorted.length > 7) {
      const top7 = sorted.slice(0, 7);
      const others = sorted.slice(7);
      const otherCount = others.reduce((sum, item) => sum + item.count, 0);

      if (otherCount > 0) {
        top7.push({ teacher: 'Others', count: otherCount });
      }

      return top7;
    }

    return sorted;
  }, [mentions]);

  // 4. Parent Response Time
  const parentResponseStats = useMemo(() => {
    if (!messages || messages.length === 0) {
      return { avgHours: null, status: 'No data' };
    }

    const responseTimes = [];

    messages.forEach(message => {
      // createdAt = when message was sent, respondedAt = when parent responded
      if (message.createdAt && message.respondedAt) {
        const sentTime = message.createdAt instanceof Date ? message.createdAt : new Date(message.createdAt);
        const readTime = message.respondedAt instanceof Date ? message.respondedAt : new Date(message.respondedAt);
        const diffMs = readTime - sentTime;
        const diffHours = diffMs / (1000 * 60 * 60);

        if (diffHours > 0 && diffHours < 168) { // Ignore if > 1 week (likely error)
          responseTimes.push(diffHours);
        }
      }
    });

    if (responseTimes.length === 0) {
      return { avgHours: null, status: 'No responses yet' };
    }

    const avgHours = responseTimes.reduce((sum, time) => sum + time, 0) / responseTimes.length;

    let status, colorClass;
    if (avgHours < 1) {
      status = 'Very responsive';
      colorClass = 'text-success';
    } else if (avgHours < 6) {
      status = 'Responsive';
      colorClass = 'text-success';
    } else if (avgHours < 24) {
      status = 'Moderately responsive';
      colorClass = 'text-warning';
    } else {
      status = 'Slow to respond';
      colorClass = 'text-error';
    }

    return { avgHours, status, colorClass };
  }, [messages]);

  // Format response time display
  const formatResponseTime = (hours) => {
    if (hours === null) return 'N/A';
    if (hours < 1) return `${Math.round(hours * 60)} min`;
    if (hours < 24) return `${hours.toFixed(1)} hrs`;
    return `${(hours / 24).toFixed(1)} days`;
  };

  // Empty state
  if (!hasEnoughData) {
    return (
      <div className="card bg-base-100 shadow mb-6 p-6">
        <div className="flex flex-col items-center justify-center py-12 text-base-content/50">
          <ChartBarIcon className="w-16 h-16 mb-4 opacity-30" />
          <p className="text-lg font-medium">Not enough data for analytics</p>
          <p className="text-sm mt-1">Analytics will appear after 3 or more mentions</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-6">
      <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
        <ChartBarIcon className="w-6 h-6" />
        Behavior Analytics
      </h3>

      {/* Grid layout for charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

        {/* 1. Mentions by Day of Week */}
        <div className="card bg-base-100 shadow p-6">
          <h4 className="text-lg font-semibold mb-4">Mentions by Day of Week</h4>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={dayOfWeekData}>
                <XAxis
                  dataKey="day"
                  tick={{ fontSize: 12 }}
                  stroke="currentColor"
                  className="text-base-content/60"
                />
                <YAxis
                  tick={{ fontSize: 12 }}
                  stroke="currentColor"
                  className="text-base-content/60"
                  allowDecimals={false}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'hsl(var(--b1))',
                    border: '1px solid hsl(var(--bc) / 0.2)',
                    borderRadius: '0.5rem'
                  }}
                  labelStyle={{ color: 'hsl(var(--bc))' }}
                />
                <Bar
                  dataKey="count"
                  fill="currentColor"
                  className="text-primary"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <p className="text-sm text-base-content/60 mt-2">
            Shows behavioral patterns across the week
          </p>
        </div>

        {/* 2. Mentions by Offense Type */}
        <div className="card bg-base-100 shadow p-6">
          <h4 className="text-lg font-semibold mb-4">Mentions by Offense</h4>
          <div className="h-64">
            {offenseData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={offenseData}
                    dataKey="count"
                    nameKey="offense"
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={70}
                    paddingAngle={2}
                    stroke="none"
                  >
                    {offenseData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--b1))',
                      border: '1px solid hsl(var(--bc) / 0.2)',
                      borderRadius: '0.5rem'
                    }}
                  />
                  <Legend
                    verticalAlign="bottom"
                    height={36}
                    iconType="circle"
                    wrapperStyle={{ fontSize: '12px' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-base-content/50">
                <p className="text-sm">No offense data available</p>
              </div>
            )}
          </div>
          <p className="text-sm text-base-content/60 mt-2">
            What offenses are being committed
          </p>
        </div>

        {/* 3. Mentions by Teacher */}
        <div className="card bg-base-100 shadow p-6">
          <h4 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <UserGroupIcon className="w-5 h-5" />
            Mentions by Teacher
          </h4>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={teacherData}
                  dataKey="count"
                  nameKey="teacher"
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={70}
                  paddingAngle={2}
                  stroke="none"
                >
                  {teacherData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={TEACHER_COLORS[index % TEACHER_COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'hsl(var(--b1))',
                    border: '1px solid hsl(var(--bc) / 0.2)',
                    borderRadius: '0.5rem'
                  }}
                />
                <Legend
                  verticalAlign="bottom"
                  height={36}
                  iconType="circle"
                  wrapperStyle={{ fontSize: '12px' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <p className="text-sm text-base-content/60 mt-2">
            Which teachers issue the most mentions
          </p>
        </div>

        {/* 4. Parent Response Time */}
        <div className="card bg-base-100 shadow p-6">
          <h4 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <ClockIcon className="w-5 h-5" />
            Parent Response Time
          </h4>
          <div className="flex flex-col items-center justify-center h-48">
            <div className="stats shadow-none bg-transparent">
              <div className="stat place-items-center p-0">
                <div className="stat-title text-base-content/60">Average Response</div>
                <div className={`stat-value text-3xl ${parentResponseStats.colorClass || 'text-base-content/40'}`}>
                  {formatResponseTime(parentResponseStats.avgHours)}
                </div>
                <div className="stat-desc text-base-content/60 mt-2">
                  {parentResponseStats.status}
                </div>
              </div>
            </div>
          </div>
          <p className="text-sm text-base-content/60 mt-2">
            Time from notification sent to parent read
          </p>
        </div>

      </div>
    </div>
  );
};

export default StudentAnalytics;
